import { STRONG_8K, NXT, MEGA_OTT, TRILLO, PLATFORM_NAMES } from "./platforms";
import { getPackagePlatformByTier, getTierColor, getPackageCreditRate } from "./packages";

/**
 * Resellers buy credits against one of the four subscriber-facing "services".
 * A service is really a vendor, and a vendor shows up under a different tier name
 * in every brand — Service FOUR is Trillo, which is Mist on Vulture and Hive on
 * Nectar. These maps are what turn a stored service code back into those names.
 *
 * Mirrors bots/centralised/consts/serviceRoles.js — keep both in step.
 * Cost rates deliberately live in Mongo (see /reseller/rates), not here.
 */
export const RESELLER_SERVICES = [
  { service: "ONE",   vendor: "NXT",       platform: NXT       },
  { service: "TWO",   vendor: "Strong 8k", platform: STRONG_8K },
  { service: "THREE", vendor: "MegaOTT",   platform: MEGA_OTT  },
  { service: "FOUR",  vendor: "Trillo",    platform: TRILLO    },
];

/** Mirrors BOT_PACKAGES in bots/centralised/consts/packages.js. */
export const BRAND_TIERS = {
  Ultimate:   ["Diamond", "Platinum", "Emerald", "Silver"],
  Experience: ["Earth", "Fire", "Water", "Wind"],
  Extreme:    ["Extreme", "Titan", "Dust", "Vengence"],
  Nectar:     ["Nectar", "Honey Pot", "Golden", "Hive"],
  Vulture:    ["Vulture", "Shadow", "Mist"],
};

export const SERVICE_CODES = RESELLER_SERVICES.map((s) => s.service);

/** The service row for a code, or null. */
export function getService(code) {
  return RESELLER_SERVICES.find((s) => s.service === code) ?? null;
}

/** Display vendor name for a service code, e.g. "FOUR" -> "Trillo". */
export function getServiceVendor(code) {
  return getService(code)?.vendor ?? "";
}

/** A service's accent colour, borrowed from its first tier so the two agree. */
export function getServiceColor(code) {
  const tiers = getServiceTiers(code);
  return tiers.length > 0 ? tiers[0].color : "#6c757d";
}

/**
 * Every tier this service appears as, across all five brands.
 * Resolved through the catalogue rather than hardcoded, so it cannot drift.
 */
export function getServiceTiers(code) {
  const service = getService(code);
  if (!service) return [];

  const vendorName = PLATFORM_NAMES[service.platform];

  return Object.entries(BRAND_TIERS).flatMap(([brand, tiers]) =>
    tiers
      .filter((tier) => getPackagePlatformByTier(tier) === vendorName)
      .map((tier) => ({
        brand,
        tier,
        color: getTierColor(tier),
        creditCost: getPackageCreditRate(tier),
      }))
  );
}

/**
 * Our cost per credit for this quantity: the ladder entry with the largest
 * minCredits that the quantity still reaches. Returns null when the service has
 * no rate card, so callers can tell "not configured" from "free".
 */
export function rateForCredits(rateDoc, credits) {
  const tier = tierForCredits(rateDoc, credits);
  return tier ? tier.costRate : null;
}

/** The ladder entry in play for this quantity, or null. */
export function tierForCredits(rateDoc, credits) {
  const tiers = rateDoc?.tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;

  const qty = Number(credits);
  const applicable = tiers
    .filter((t) => Number(t.minCredits) <= (Number.isFinite(qty) ? qty : 0))
    .sort((a, b) => Number(a.minCredits) - Number(b.minCredits));

  return applicable.length > 0 ? applicable[applicable.length - 1] : null;
}

/** Human label for the ladder entry in play, e.g. "rate card: 200+ credits". */
export function describeTier(rateDoc, credits) {
  const tier = tierForCredits(rateDoc, credits);
  if (!tier) return null;
  return Number(tier.minCredits) === 0
    ? "rate card: base rate"
    : `rate card: ${tier.minCredits}+ credits`;
}

/** Turns the rates endpoint's array into a { [service]: doc } lookup. */
export function indexRates(rates) {
  return (Array.isArray(rates) ? rates : []).reduce((acc, r) => {
    acc[r.service] = r;
    return acc;
  }, {});
}

/** <option> elements for a service picker, in the style of PackageTierSelect. */
export function ServiceSelect() {
  return (
    <>
      <option value="">Select service…</option>
      {RESELLER_SERVICES.map(({ service, vendor }) => (
        <option key={service} value={service}>Service {service} · {vendor}</option>
      ))}
    </>
  );
}
